import type { FC } from 'react';
import { useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { IconSolidCheveronDown, IconSolidCheveronUp } from '@gonfalon/observability-ui/components/icons';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { themeVars } from '@gonfalon/observability-ui/theme';

import { useAgentInsights } from '../../../../../AgentInsightsContext';
import { VegaMarkdownRenderer } from '../artifacts/VegaMarkdownRenderer';

type PlanModeInput = {
  plan?: string;
  planFilePath?: string;
  allowedPrompts?: Array<{ tool: string; prompt: string }>;
};

export const PlanModeMessage: FC<{
  variant: 'enter' | 'exit';
  toolInputs?: unknown;
}> = ({ variant, toolInputs }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { projectKey } = useProjectId();
  const { startConversationWithSource } = useAgentInsights();

  const isEnter = variant === 'enter';
  const label = isEnter ? msg('vega.conversation.planDeveloping') : msg('vega.conversation.planFinalized');

  const input = (!isEnter && toolInputs ? toolInputs : null) as PlanModeInput | null;
  const plan = input?.plan;
  const allowedPrompts = input?.allowedPrompts?.filter(
    (p): p is { tool: string; prompt: string } => typeof p === 'object' && p !== null && typeof p.prompt === 'string',
  );

  const hasDetails = Boolean(plan) || (allowedPrompts && allowedPrompts.length > 0);
  const expanded = isExpanded && hasDetails;

  return (
    <Box marginBottom="12">
      <Box
        border="divider"
        cursor={hasDetails ? 'pointer' : 'default'}
        py="8"
        px="10"
        onClick={hasDetails ? () => setIsExpanded(!isExpanded) : undefined}
        style={{
          borderRadius: expanded ? '6px 6px 0 0' : '6px',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="6">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                fontSize: '11px',
                backgroundColor: isEnter
                  ? themeVars.static.divider.default
                  : themeVars.interactive.fill.positive.enabled,
                color: isEnter
                  ? themeVars.static.content.default
                  : themeVars.interactive.fill.positive.content.onEnabled,
                flexShrink: 0,
              }}
            >
              {isEnter ? '▶' : '✓'}
            </span>
            <Text size="small" weight="bold">
              {label}
            </Text>
          </Box>
          {hasDetails &&
            (isExpanded ? (
              <IconSolidCheveronUp style={{ width: '14px', height: '14px', opacity: 0.6 }} />
            ) : (
              <IconSolidCheveronDown style={{ width: '14px', height: '14px', opacity: 0.6 }} />
            ))}
        </Box>
      </Box>

      {expanded && (
        <Box
          border="divider"
          borderTop="none"
          padding="10"
          style={{
            borderRadius: '0 0 6px 6px',
            maxHeight: '400px',
            overflow: 'auto',
          }}
        >
          {plan && (
            <VegaMarkdownRenderer
              text={plan}
              projectKey={projectKey}
              onExternalSourceClick={(match) => startConversationWithSource(match.url, match.sourceType)}
            />
          )}

          {allowedPrompts && allowedPrompts.length > 0 && (
            <>
              <Text size="xSmall" weight="bold" color="weak">
                <Message id="vega.conversation.plannedActions" />
              </Text>
              <Box marginTop="6" display="flex" flexDirection="column" gap="4">
                {allowedPrompts.map((prompt, i) => (
                  <Box
                    backgroundColor="elevated"
                    borderRadius="4"
                    key={i}
                    display="flex"
                    alignItems="center"
                    gap="6"
                    px="8"
                    py="6"
                  >
                    <Text size="xSmall" family="monospace" color="weak">
                      {prompt.tool}
                    </Text>
                    <Text size="xSmall">{prompt.prompt}</Text>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
